import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Gateway from "@/pages/Gateway";
import RealEstateLanding from "@/pages/realestate/RealEstateLanding";
import PropertyPage from "@/pages/realestate/PropertyPage";
import InvestmentPage from "@/pages/realestate/InvestmentPage";
import ITManagement from "@/pages/it/ITManagement";
import DigitalServices from "@/pages/digital/DigitalServices";
import IncGateway from "@/pages/inc/IncGateway";
import CommerceGroup from "@/pages/commerce/CommerceGroup";
import ProductPage from "@/pages/commerce/ProductPage";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Support from "@/pages/legal/Support";
import { ON_COMMERCE_HOST, ON_DIGITAL_HOST, ON_EYES_HOST, ON_PRODUCT_HOST, PRODUCT_HOST } from "@/lib/routes";

function SmoothScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function HomePage() {
  if (ON_PRODUCT_HOST) return <ProductPage index={PRODUCT_HOST} />;
  if (ON_COMMERCE_HOST) return <CommerceGroup />;
  if (ON_DIGITAL_HOST) return <DigitalServices />;
  if (ON_EYES_HOST) return <Gateway />;
  // Eagle Inc is the root of everything: eagle-inc.* AND every other/unknown host (preview, localhost)
  return <IncGateway />;
}

// Per-hostname canonical tag so each domain points to itself for SEO.
function Canonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${window.location.origin}${pathname}`;
  }, [pathname]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <SmoothScroll />
        <Canonical />
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Hierarchy paths — work on every host; on eagle-inc.* they nest under /eagle-eyes */}
          <Route path="/inc" element={<IncGateway />} />
          <Route path="/commerce-group" element={<CommerceGroup />} />
          <Route path="/eagle-commerce" element={<CommerceGroup />} />
          <Route path="/eagle-commerce/socialdeal" element={<ProductPage index={0} />} />
          <Route path="/eagle-commerce/bookitnow" element={<ProductPage index={1} />} />
          <Route path="/eagle-commerce/incontact" element={<ProductPage index={2} />} />
          <Route path="/socialdeal" element={<ProductPage index={0} />} />
          <Route path="/bookitnow" element={<ProductPage index={1} />} />
          <Route path="/incontact" element={<ProductPage index={2} />} />
          <Route path="/eagle-eyes" element={<Gateway />} />
          <Route path="/eagle-eyes/real-estate" element={<RealEstateLanding />} />
          <Route path="/eagle-eyes/real-estate/property" element={<PropertyPage />} />
          <Route path="/eagle-eyes/real-estate/investment" element={<InvestmentPage />} />
          <Route path="/eagle-eyes/it-management" element={<ITManagement />} />
          <Route path="/eagle-eyes/digital-services" element={<DigitalServices />} />
          {/* Eagle Eyes Group canonical paths on eagle-eyes.al */}
          <Route path="/realestate" element={<RealEstateLanding />} />
          <Route path="/realestate/property" element={<PropertyPage />} />
          <Route path="/realestate/investment" element={<InvestmentPage />} />
          {/* Legacy paths redirect to the canonical ones */}
          <Route path="/real-estate" element={<Navigate to="/realestate" replace />} />
          <Route path="/real-estate/property" element={<Navigate to="/realestate/property" replace />} />
          <Route path="/real-estate/investment" element={<Navigate to="/realestate/investment" replace />} />
          <Route path="/property" element={<Navigate to="/realestate/property" replace />} />
          <Route path="/investment" element={<Navigate to="/realestate/investment" replace />} />
          <Route path="/it-management" element={<ITManagement />} />
          <Route path="/digital-services" element={ON_DIGITAL_HOST ? <Navigate to="/" replace /> : <DigitalServices />} />
          <Route path="/digitalservices" element={<Navigate to="/digital-services" replace />} />
          {/* Legal & support — available on every domain (required by the acquiring bank / card schemes) */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/termsofuse" element={<Terms />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms" element={<Navigate to="/termsofuse" replace />} />
          <Route path="/terms-of-use" element={<Navigate to="/termsofuse" replace />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
